import { atom, useAtom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
/* oxlint-disable react/react-compiler -- Existing code is not yet compatible with React Compiler checks. */
import * as React from 'react';
import type { DragItem, Key } from 'react-aria';

import type * as api from '@gitbook/api-client';
import { createContentMoveEvent, logEvent } from '@gitbook/app-analytics/client';
import type { ContentOwnership } from '@gitbook/app-database';
import type { NavigationScreenState } from '@gitbook/app-navigation';
import { createPalette, PaletteButton, usePaletteTrigger } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import {
    Collection,
    HeadlessTreeItemContent,
    SidebarActionButton,
    SidebarButton,
    SidebarItemIcon,
    SidebarTree,
    SidebarTreeItem,
    SidebarTreeItemExpandButton,
    SidebarTreeItemText,
    toast,
    useDragAndDrop,
    useScreenType,
} from '@gitbook/spine-react';
import { assert, assertNever, filterOutNullable } from '@gitbook/util-ts';

import {
    getCollectionFromOrgAllContent,
    getSpaceFromOrgAllContent,
    useCollectionPalette,
    useCreateCollectionCommand,
    useCreateSpaceCommands,
    useSpaceActionsCommands,
} from '../../Components';
import type { CollectionItem, ContentItem } from '../../state';
import { apiClient, getContentItems } from '../../state';
import { SidebarActionsButton } from '../SidebarActionsButton';
import { SIDEBAR_VISIBLE_ITEMS_DEFAULT } from './ContentSidebar.utils';

export function CreateContentButton(props: {
    organizationId: string;
    kind?: 'default' | 'sidebar';
}) {
    const { organizationId, kind = 'default' } = props;
    const owner = { organization: organizationId };
    const [{ newSpace }, createSpaceElements] = useCreateSpaceCommands(owner);
    const [newCollectionCommand, createCollectionElements] = useCreateCollectionCommand(owner);

    const palette = React.useMemo(() => {
        return createPalette({
            groups: [
                {
                    key: 'owner',
                    commands: [newSpace, newCollectionCommand].filter(filterOutNullable),
                },
            ],
        });
    }, [newSpace, newCollectionCommand]);

    const { triggerProps } = usePaletteTrigger<HTMLDivElement>({
        palette: () => palette,
    });

    const { isMobileOrTablet } = useScreenType();

    return (
        <>
            {kind === 'sidebar' ? (
                <SidebarActionButton label="Create content" icon={Icon.Plus} {...triggerProps} />
            ) : (
                <PaletteButton
                    kind="primary"
                    label="Create content"
                    leadingIcon={Icon.Plus}
                    palette={() => palette}
                    iconOnly={isMobileOrTablet}
                />
            )}
            {createSpaceElements}
            {createCollectionElements}
        </>
    );
}

function getItem(key: Key, inItems: ContentItem[]): null | ContentItem {
    for (const item of inItems) {
        if (item.id === key) {
            return item;
        }
        if ('children' in item) {
            const foundItem = getItem(key, item.children);
            if (foundItem) {
                return foundItem;
            }
        }
    }
    return null;
}

function getInitialItems(items: ContentItem[], activeSpaceId: string | null): ContentItem[] {
    const initialItems = items.slice(0, SIDEBAR_VISIBLE_ITEMS_DEFAULT);
    if (!activeSpaceId || getItem(activeSpaceId, initialItems)) {
        return initialItems;
    }

    const activeRootItem = items.find((item) => getItem(activeSpaceId, [item]));
    if (!activeRootItem) {
        return initialItems;
    }

    return [...initialItems.slice(0, -1), activeRootItem];
}

function getParent(key: Key, inItems: ContentItem[]): null | CollectionItem {
    for (const item of inItems) {
        if ('children' in item) {
            const found = item.children.some((child) => child.id === key);
            if (found) {
                return item;
            }
            const parent = getParent(key, item.children);
            if (parent) {
                return parent;
            }
        }
    }
    return null;
}

function getContentPointer(item: ContentItem): api.SpacePointer | api.CollectionPointer {
    switch (item.type) {
        case 'space':
            return {
                type: 'space',
                space: item.space.space.id,
            };
        case 'collection':
            return {
                type: 'collection',
                collection: item.collection.collection.id,
            };
        default:
            assertNever(item);
    }
}

/**
 * Get the ancestors of a content item.
 */
function getAncestors(content: api.OrganizationAllContent, item: ContentItem) {
    switch (item.type) {
        case 'space':
            return getSpaceFromOrgAllContent(content, item.space.space.id)?.ancestors ?? null;
        case 'collection':
            return (
                getCollectionFromOrgAllContent(content, item.collection.collection.id)?.ancestors ??
                null
            );
        default:
            assertNever(item);
    }
}

async function moveContent(props: {
    item: ContentItem;
    origin: ContentOwnership;
    destination: ContentOwnership;
    position?: api.ContentPosition;
}) {
    const { item, origin, destination } = props;
    switch (item.type) {
        case 'space':
            await toast.catch(async () => {
                await apiClient.spaces.moveSpace(item.space.space.id, {
                    parent: destination.collection ?? null,
                    position: props.position,
                });
                logEvent(
                    createContentMoveEvent(origin, destination, 'drag-drop', {
                        type: 'space_move',
                        space: item.id,
                        organization: origin.organization,
                    })
                );
            }, 'Error while moving space');
            return;
        case 'collection': {
            await toast.catch(async () => {
                await apiClient.collections.moveCollection(item.collection.collection.id, {
                    parent: destination.collection,
                    position: props.position,
                });

                logEvent(
                    createContentMoveEvent(origin, destination, 'drag-drop', {
                        type: 'collection_move',
                        collection: item.id,
                        organization: origin.organization,
                    })
                );
            }, 'Error while moving collection');
            return;
        }
        default:
            assertNever(item);
    }
}

const expandedContentItemKeysAtom = atomWithStorage<Key[]>('sidebarSpacesExpanded', [], undefined, {
    getOnInit: true,
});

export const expandedContentItemsAtom = atom(
    (get) => new Set(get(expandedContentItemKeysAtom)),
    (get, set, update: Set<Key> | ((previous: Set<Key>) => Set<Key>)) => {
        const previous = new Set(get(expandedContentItemKeysAtom));
        const next = typeof update === 'function' ? update(previous) : update;
        if (areSetsEqual(previous, next)) {
            return;
        }
        set(expandedContentItemKeysAtom, Array.from(next));
    }
);

/**
 * Organization content tree used by sidebars that need to render spaces and collections.
 */
export function SpacesTree(props: {
    organization: api.Organization;
    content: api.OrganizationAllContent;
    screen: NavigationScreenState;
}) {
    const { content, organization, screen } = props;
    const items = React.useMemo(() => getContentItems(content.spaces), [content.spaces]);
    const [draggedKeys, setDraggedKeys] = React.useState(new Set<Key>());
    const [expandedKeys, setExpandedKeys] = useAtom(expandedContentItemsAtom);
    const [showAllItems, setShowAllItems] = React.useState(false);
    const activeSpaceId = screen.type === 'space' ? screen.space : null;
    const visibleItems = React.useMemo(
        () => (showAllItems ? items : getInitialItems(items, activeSpaceId)),
        [activeSpaceId, items, showAllItems]
    );

    const { dragAndDropHooks } = useDragAndDrop({
        isDisabled: !organization.permissions.createContent,
        onDragStart: (event) => {
            setDraggedKeys(event.keys);
        },
        onDragEnd: () => {
            setDraggedKeys(new Set());
        },
        // Open the item when we drag over it.
        onDropActivate: (event) => {
            // If we drop on an item, select it.
            if (event.target.type === 'item' && event.target.dropPosition === 'on') {
                // If not already expanded.
                if (!expandedKeys.has(event.target.key)) {
                    setExpandedKeys(new Set(expandedKeys).add(event.target.key));
                }
            }
        },
        getItems: (keys) => {
            return Array.from(keys).map((key) => {
                const item = getItem(key, items);
                assert(item);
                const data: DragItem = {
                    'x-gitbook/item-key': String(item.id),
                    'text/plain': item.title,
                };
                return data;
            });
        },
        // We could support copy but it's really a nice to have for this particular use case.
        getAllowedDropOperations: () => ['move'],
        getDropOperation(target, _types, allowedOperations) {
            // Support only item drops, not root.
            if (target.type !== 'item') {
                return 'cancel';
            }

            // Pick the best allowed operation.
            const bestAllowedOperation = allowedOperations[0];

            // If we drag on the same item, cancel the operation.
            if (draggedKeys.has(target.key)) {
                return 'cancel';
            }

            // If we want to drag just after an expanded item, cancel the operation.
            if (target.dropPosition === 'after' && expandedKeys.has(target.key)) {
                return 'cancel';
            }

            // We support only one dragged item.
            if (draggedKeys.size !== 1) {
                return 'cancel';
            }

            const targetItem = getItem(target.key, items);

            const draggedKey = Array.from(draggedKeys)[0];
            const draggedItem = getItem(draggedKey, items);

            // If we can't find the item, cancel the operation.
            if (!targetItem || !draggedItem) {
                return 'cancel';
            }

            if (draggedItem.type === 'space') {
                if (!draggedItem.space.space.permissions.admin) {
                    return 'cancel';
                }

                // Disallow to drop a space on its parent.
                if (
                    target.dropPosition === 'on' &&
                    targetItem.type === 'collection' &&
                    draggedItem.space.space.parent === targetItem.collection.collection.id
                ) {
                    return 'cancel';
                }
            }

            if (draggedItem.type === 'collection') {
                if (!draggedItem.collection.collection.permissions.admin) {
                    return 'cancel';
                }

                const targetAncestors = getAncestors(content, targetItem);

                if (targetAncestors === null) {
                    return 'cancel';
                }

                // If we try to drop on a collection that is a child of the dragged item, cancel the operation.
                if (targetAncestors.some((ancestor) => ancestor.collection.id === draggedItem.id)) {
                    return 'cancel';
                }

                // Disallow to drop a collection on its parent.
                if (
                    target.dropPosition === 'on' &&
                    targetItem.type === 'collection' &&
                    draggedItem.collection.collection.parent === targetItem.collection.collection.id
                ) {
                    return 'cancel';
                }
            }

            // Drag on an item.
            if (target.dropPosition === 'on') {
                // We can only drop on a collection.
                if (targetItem.type !== 'collection') {
                    return 'cancel';
                }

                return bestAllowedOperation;
            }

            if (targetItem.type !== draggedItem.type) {
                // We can only move items of the same type.
                return 'cancel';
            }

            return bestAllowedOperation;
        },
        onItemDrop: async (event) => {
            switch (event.dropOperation) {
                case 'move': {
                    if (!event.isInternal) {
                        return;
                    }
                    const keys = (
                        await Promise.all(
                            event.items.map(async (item) => {
                                if (item.kind !== 'text') {
                                    return null;
                                }
                                if (!item.types.has('x-gitbook/item-key')) {
                                    return null;
                                }
                                const text = await item.getText('x-gitbook/item-key');
                                return text;
                            })
                        )
                    ).filter(filterOutNullable);
                    const key = keys[0];
                    if (!key) {
                        return;
                    }
                    const item = getItem(key, items);
                    const targetItem = getItem(event.target.key, items);
                    if (!item || !targetItem || targetItem.type !== 'collection') {
                        return;
                    }
                    const itemParent = getParent(item.id, items);
                    await moveContent({
                        origin: {
                            organization: organization.id,
                            collection: itemParent?.collection.collection.id,
                        },
                        destination: {
                            organization: organization.id,
                            collection: targetItem.collection.collection.id,
                        },
                        item,
                    });
                    return;
                }
                default:
                    throw new Error(`Unsupported drop operation: ${event.dropOperation}`);
            }
        },
        onMove: (event) => {
            // React Aria calls onMove for all internal drops, including "on" targets.
            // The "on" case is handled by onItemDrop above.
            if (event.target.dropPosition === 'on') {
                return;
            }

            switch (event.dropOperation) {
                case 'move': {
                    // We support only one dragged item.
                    if (event.keys.size !== 1) {
                        return;
                    }
                    const movedKey = Array.from(event.keys)[0];
                    const movedItem = getItem(movedKey, items);
                    const targetItem = getItem(event.target.key, items);
                    if (!targetItem || !movedItem) {
                        return;
                    }
                    const movedParent = getParent(movedKey, items);
                    const targetParent = getParent(event.target.key, items);
                    const targetParentItems = targetParent?.children ?? items;
                    const targetIndex = targetParentItems.indexOf(targetItem);
                    const moveToIndex =
                        targetIndex + (event.target.dropPosition === 'before' ? 0 : 1);
                    const moveAfterItem = targetParentItems[moveToIndex - 1];
                    const moveBeforeItem = targetParentItems[moveToIndex];

                    moveContent({
                        origin: {
                            organization: organization.id,
                            collection: movedParent?.collection.collection.id,
                        },
                        destination: {
                            organization: organization.id,
                            collection: targetParent?.collection.collection.id,
                        },
                        item: movedItem,
                        position: {
                            before: moveAfterItem ? getContentPointer(moveAfterItem) : undefined,
                            after: moveBeforeItem ? getContentPointer(moveBeforeItem) : undefined,
                        },
                    });
                    return;
                }
                default:
                    throw new Error(`Unsupported drop operation: ${event.dropOperation}`);
            }
        },
    });

    return (
        <>
            <SidebarTree<ContentItem>
                // Temporary workaround for a bug when switching between orgs
                key={'organization' in screen ? screen.organization : undefined}
                dependencies={[screen.type, screen.type === 'space' ? screen.space : null]}
                items={visibleItems}
                aria-label="Spaces"
                expandedKeys={expandedKeys}
                onExpandedChange={setExpandedKeys}
                dragAndDropHooks={dragAndDropHooks}
            >
                {function renderItem(treeItem) {
                    const isExpandable = 'children' in treeItem && treeItem.children.length > 0;
                    const href = 'href' in treeItem ? treeItem.href : undefined;
                    const isActive = 'isActive' in treeItem ? treeItem.isActive : undefined;
                    return (
                        <SidebarTreeItem href={href} isActive={isActive?.(screen)} {...props}>
                            <ContentSidebarTreeItemContent
                                value={treeItem}
                                isExpandable={isExpandable}
                            />
                            {'children' in treeItem ? (
                                <Collection items={treeItem.children}>{renderItem}</Collection>
                            ) : null}
                        </SidebarTreeItem>
                    );
                }}
            </SidebarTree>
            {items.length > SIDEBAR_VISIBLE_ITEMS_DEFAULT ? (
                <SidebarButton
                    label={showAllItems ? 'Show less' : 'Show all'}
                    icon={showAllItems ? Icon.Minus : Icon.MoreHorizontal}
                    onPress={() => setShowAllItems((showAll) => !showAll)}
                />
            ) : null}
        </>
    );
}

const ContentSidebarTreeItemContent = React.memo(function ContentSidebarTreeItemContent(props: {
    value: ContentItem;
    isExpandable: boolean;
}) {
    const { value, isExpandable } = props;
    return (
        <HeadlessTreeItemContent>
            {isExpandable ? (
                <SidebarTreeItemExpandButton icon={value.icon} />
            ) : (
                <SidebarItemIcon icon={value.icon} />
            )}
            <SidebarTreeItemText>{value.title}</SidebarTreeItemText>
            <ContentSidebarTreeActions value={value} />
        </HeadlessTreeItemContent>
    );
});

function ContentSidebarTreeActions(props: { value: ContentItem }) {
    const { value } = props;
    switch (value.type) {
        case 'space':
            return <ContentSidebarSpaceActions space={value.space.space} />;
        case 'collection':
            return <ContentSidebarCollectionActions collection={value.collection.collection} />;
        default:
            assertNever(value);
    }
}

function ContentSidebarSpaceActions(props: { space: api.Space }) {
    const { space } = props;
    const spaceActions = useSpaceActionsCommands(space);

    return (
        <>
            <SidebarActionsButton
                palette={() => createPalette({ groups: [spaceActions.paletteGroup()] })}
            />
            {spaceActions.dialogs}
        </>
    );
}

function ContentSidebarCollectionActions(props: { collection: api.Collection }) {
    const { collection } = props;
    const [palette, elements] = useCollectionPalette(collection);

    return (
        <>
            <SidebarActionsButton palette={palette} />
            {elements}
        </>
    );
}

function areSetsEqual<T>(left: Set<T>, right: Set<T>): boolean {
    return left.size === right.size && Array.from(left).every((key) => right.has(key));
}
